import service from "@/utils/request";

/**
 * 价格信息接口
 */
export interface PackagePriceData {
    /** 原价 */
    originalPrice?: number | string
    /** 活动价 */
    activityPrice?: number | string
    /** 会员价 */
    memberPrice?: number | string
    /** 月卡价格 */
    monthlyPrice?: number | string
    /** 季卡价格 */
    quarterlyPrice?: number | string
    /** 年卡价格 */
    yearlyPrice?: number | string
    /** 折扣（0-10） */
    discount?: number | string
    /** 满减规则 */
    fullReduction?: string
    /** 服务点价格 */
    servicePointPrice?: number | null
}

/**
 * 规格信息接口
 */
export interface PackageSpecData {
    /** 自定义规格Map，key为规格类型，value为规格选项列表 */
    specTypes?: Record<string, string[]>
}

/**
 * 评价统计接口
 */
export interface PackageCommentData {
    /** 平均评分（1-5星） */
    averageRating?: number
    /** 评价标签列表 */
    commentTags?: string[]
    /** 服务人数 */
    serviceCount?: number
    /** 总评价数 */
    totalComments?: number
    /** 好评数 */
    goodComments?: number
    /** 中评数 */
    mediumComments?: number
    /** 差评数 */
    badComments?: number
}

/**
 * 服务内容配置接口
 */
export interface PackageContentData {
    /** 副标题 */
    subtitle?: string
    /** 服务简介 */
    introduction?: string
    /** 适用人群 */
    applicablePeople?: string
    /** 上架状态 */
    shelfStatus?: boolean
    /** 上架时间 */
    shelfTime?: string | Date
    /** 下架时间 */
    unshelfTime?: string | Date
    /** 库存数量 */
    stock?: number
    /** 限购数量 */
    purchaseLimit?: number
    /** 所属服务商ID */
    serviceProviderId?: string
    /** 所属服务商名称 */
    serviceProviderName?: string
    /** 接单区域（城市/区域） */
    serviceAreas?: string[]
    /** 服务适用慢病病种 */
    applicableDiseases?: string[]
    /** 服务有效期（天） */
    validityDays?: number
    /** 退款规则 */
    refundRule?: string
    /** 预约须知 */
    appointmentNotice?: string
    /** 服务时间 */
    serviceTime?: string
    /** 服务范围描述 */
    serviceScope?: string
    /** 履约方式：线上咨询/上门服务/到店服务/居家监测 */
    fulfillmentMethods?: string[]
}

/**
 * 慢病服务包实体接口
 */
export interface DoctorChronicPackageEntity {
    /** 服务包ID */
    id?: number

    /** 服务包名称 */
    name: string

    /** 分类信息 */
    classify?: string[],

    /** 封面图片地址 */
    cover: string

    /** 所属单位ID */
    orgId: string

    /** 管理员ID */
    adminId: string

    /** 服务内容配置 */
    packageContent?: PackageContentData

    /** 价格信息 */
    price?: PackagePriceData

    /** 标签列表 */
    tags?: string[]

    /** 图片列表 */
    pics?: string[]

    /** 关联医生ID列表 */
    doctorLinks?: string[]

    /** 产品ID */
    productId?: string,

    /** 售卖方式(字典payment_sale_type) */
    saleType?: string

    /** 规格 */
    spec?: PackageSpecData

    /** 评价统计 */
    comments?: PackageCommentData

    /** 其他选项配置 */
    options?: Record<string, any>

    /** 服务包描述 */
    description?: string

    /** 状态（启用/禁用） */
    status?: boolean

    /** 版本号 */
    version?: number

    /** 删除标识（0：正常，1：已删除） */
    deleted?: number

    /** 创建人ID */
    createId?: string

    /** 更新人ID */
    updateId?: string

    /** 创建时间 */
    createTime?: string | Date

    /** 更新时间 */
    updateTime?: string | Date
}

export const useChronicPackageSubmitApi = (dataForm: DoctorChronicPackageEntity) => {
    if (dataForm.id) {
        return service.put('/doctor/chronic/package', dataForm)
    } else {
        return service.post('/doctor/chronic/package', dataForm)
    }
}

export const useChronicPackageInfoApi = (id: string) => {
    return service.get<DoctorChronicPackageEntity>('/doctor/chronic/package/' + id)
}


/**
 * 慢病服务包订单实体接口
 */
export interface DoctorChronicPackageOrderEntity {
    /** 订单ID */
    id?: number

    /** 订单号 */
    orderNo: string

    /** 服务包ID */
    packageId: string

    /** 用户ID */
    userId: string

    /** 购买用户姓名/昵称，缺失时前端回退用户 ID */
    userName?: string

    /** 服务包名称，缺失时前端回退服务包 ID */
    packageName?: string

    /** 履约医生姓名/昵称，缺失时前端回退医生 ID */
    doctorName?: string

    /** 支付状态 */
    payStatus?: boolean

    /** 支付时间 */
    payTime?: string | Date

    /** 支付金额 */
    payAmount?: number | string

    /** 支付类型（如：微信、支付宝等） */
    payType?: string

    /** 服务点消耗 */
    servicePointCost?: number

    /** 第三方支付订单号 */
    payOrderNo?: string

    /** 支付详细信息 */
    payInfo?: Record<string, any>

    /** 支付备注 */
    payRemark?: string

    /** 关联医生ID */
    doctorId?: string

    /** 医生备注 */
    doctorRemark?: string

    /** 签约状态 */
    signStatus?: boolean

    /** 签约时间 */
    signTime?: string | Date

    /** 签约到期时间 */
    signEnd?: string | Date

    /** 签约备注 */
    signRemark?: string

    /** 签约图片列表 */
    signPics?: string[]

    /** 其他选项配置，人工受理信息见 options.fulfillment */
    options?: Record<string, any>

    /** 订单描述 */
    description?: string

    /** 订单状态 */
    status?: boolean

    /** 版本号 */
    version?: number

    /** 删除标识（0：正常，1：已删除） */
    deleted?: number

    /** 创建人ID */
    createId?: string

    /** 更新人ID */
    updateId?: string

    /** 创建时间 */
    createTime?: string | Date

    /** 更新时间 */
    updateTime?: string | Date
}

const test1 = ["17717169797", "18698000481", "18279625528", "17717169797", "18698000481", "18279625528"]
const test2 = ["芳园", "深圳芳园天伦科技", "北洋物联", "吾家智能", "Fytlun", "芳园天伦"]
const test3 = [{"code": "233333"}, {"code": "233333"}, {"code": "233333"}, {"code": "233333"}, {"code": "233333"}, {"code": "233333"}]


/**
 * 慢病服务包分类实体接口
 */
export interface DoctorChronicPackageClassifyEntity {
    /** 分类ID */
    id?: number

    /** 父级ID */
    parentId?: number

    /** 层级：1-系统一级，2-系统二级，3-服务商自定义 */
    level?: number

    /** 分类名称 */
    name: string

    /** 分类编码 */
    code?: string

    /** 排序号 */
    sort?: number

    /** 是否系统预设 */
    isSystem?: boolean

    /** 服务商ID */
    providerId?: string

    /** 状态 */
    status?: boolean

    /** 子分类列表（树形结构） */
    children?: DoctorChronicPackageClassifyEntity[]

    /** 创建时间 */
    createTime?: string | Date

    /** 更新时间 */
    updateTime?: string | Date
}

/**
 * 获取分类树形结构
 */
export const useChronicPackageClassifyTreeApi = () => {
    // 不接受客户端 providerId，当前机构由服务端验证并确定。
    return service.get<DoctorChronicPackageClassifyEntity[]>('/doctor/chronic/package/classify/tree')
}

/**
 * 新增分类
 */
export const useChronicPackageClassifyAddApi = (dataForm: DoctorChronicPackageClassifyEntity) => {
    return service.post('/doctor/chronic/package/classify', dataForm)
}

/**
 * 修改分类
 */
export const useChronicPackageClassifyUpdateApi = (dataForm: DoctorChronicPackageClassifyEntity) => {
    return service.put('/doctor/chronic/package/classify', dataForm)
}

/**
 * 删除分类
 */
export const useChronicPackageClassifyDeleteApi = (id: number) => {
    return service.delete(`/doctor/chronic/package/classify/${id}`)
}

/**
 * 获取订单详情
 */
export const useChronicPackageOrderInfoApi = (id: number) => {
    return service.get<DoctorChronicPackageOrderEntity>(`/doctor/chronic/order/${id}`)
}

/**
 * 新增或修改订单
 */
export const useChronicPackageOrderSubmitApi = (dataForm: DoctorChronicPackageOrderEntity) => {
    if (dataForm.id) {
        return service.put('/doctor/chronic/order', dataForm)
    } else {
        return service.post('/doctor/chronic/order', dataForm)
    }
}

/**
 * 删除订单
 */
export const useChronicPackageOrderDeleteApi = (idList: number[]) => {
    return service.delete('/doctor/chronic/order', { data: idList })
}

/**
 * 通用内容模板实体接口
 */
export interface DoctorChronicPackageTemplateEntity {
    /** 模板ID */
    id?: number

    /** 模板名称 */
    templateName: string

    /** 模板类型：knowledge-知识库, disease-病种配置, indicator-指标配置, form-表单配置, package-服务包模板 */
    templateType: string

    /** 分类（次级分类） */
    category?: string

    /** 服务商ID（可选，为空表示系统预设） */
    providerId?: string

    /** 是否系统预设 */
    isSystem?: boolean

    /** 内容数据（JSON格式） */
    contentData?: Record<string, any>

    /** 摘要/简介 */
    summary?: string

    /** 封面图片地址 */
    coverImage?: string

    /** 标签列表 */
    tags?: string[]

    /** 浏览次数 */
    viewCount?: number

    /** 使用次数 */
    usageCount?: number

    /** 排序号 */
    sort?: number

    /** 描述 */
    description?: string

    /** 状态 */
    status?: boolean

    /** 创建时间 */
    createTime?: string | Date

    /** 更新时间 */
    updateTime?: string | Date
}

/**
 * 获取模板分页列表
 */
export const useTemplatePageApi = (params: any) => {
    return service.get('/doctor/chronic/package/template/page', { params })
}

/**
 * 按类型获取模板列表
 */
export const useTemplateByTypeApi = (templateType: string, category?: string) => {
    return service.get<DoctorChronicPackageTemplateEntity[]>(`/doctor/chronic/package/template/type/${templateType}`, {
        params: category ? { category } : {}
    })
}

/**
 * 获取模板详情
 */
export const useTemplateInfoApi = (id: number) => {
    return service.get<DoctorChronicPackageTemplateEntity>(`/doctor/chronic/package/template/${id}`)
}

/**
 * 新增或修改模板
 */
export const useTemplateSubmitApi = (dataForm: DoctorChronicPackageTemplateEntity) => {
    if (dataForm.id) {
        return service.put('/doctor/chronic/package/template', dataForm)
    } else {
        return service.post('/doctor/chronic/package/template', dataForm)
    }
}

/**
 * 删除模板
 */
export const useTemplateDeleteApi = (idList: number[]) => {
    return service.delete('/doctor/chronic/package/template', { data: idList })
}

/**
 * 增加模板浏览次数
 */
export const useTemplateIncrementViewApi = (id: number) => {
    return service.put(`/doctor/chronic/package/template/${id}/view`)
}




/** 从已支付慢病订单建立或复用客服 IM 会话。由后端校验机构管理员/关联医生权限。 */
export const useChronicOrderContactApi = (orderId: number) => {
    return service.post<string>(`/doctor/chronic/order/${orderId}/contact`)
}

/** 只有当前订单所属机构管理员可以获得可分配医生候选人。 */
export interface ChronicAssignableDoctor {
    userId: string
    name: string
}

export const useChronicOrderAssignableDoctorsApi = (orderId: number) => {
    return service.get<ChronicAssignableDoctor[]>(`/doctor/chronic/order/${orderId}/assignable-doctors`)
}

/** 一次确认服务并分配医生。支付状态与月度权益不会被这个接口修改。 */
export const useChronicOrderAcceptApi = (orderId: number, doctorId: string, remark?: string) => {
    return service.post<DoctorChronicPackageOrderEntity>(
        `/doctor/chronic/order/${orderId}/accept`,
        { doctorId, remark }
    )
}

/** 医生端最小履约订单视图：服务端仅返回当前登录医生已分配、已确认订单。 */
export interface DoctorAssignedOrderResult {
    orderId: number
    orderNo: string
    packageName: string
    patientName: string
    patientId: string
    serviceStartTime?: string | Date
    serviceEndTime?: string | Date
    acceptanceRemark?: string
    acceptedAt?: string
}

export interface DoctorAssignedOrderPage {
    list: DoctorAssignedOrderResult[]
    total: number
}

/** 注意：不接受 doctorId 参数，身份从服务端认证上下文读取。 */
export const useDoctorAssignedOrderPageApi = (params: { page: number; limit: number; orderNo?: string }) => {
    return service.get<DoctorAssignedOrderPage>('/doctor/chronic/order/my/page', { params })
}

export const useDoctorAssignedOrderInfoApi = (orderId: number) => {
    return service.get<DoctorAssignedOrderResult>(`/doctor/chronic/order/my/${orderId}`)
}

export const useDoctorAssignedOrderContactApi = (orderId: number) => {
    return service.post<string>(`/doctor/chronic/order/my/${orderId}/contact`)
}

/** 只用于隐藏 Legacy 机构管理菜单；真正的读写授权必须由后端接口逐项校验。 */
export const useChronicAdminAccessApi = () => {
    return service.get<boolean>('/doctor/chronic/order/admin/access')
}

/** 咨询会话是订单级业务记录，不依赖 UniIM 原始消息数量。 */
export interface ChronicConsultation {
    id: number
    orderId: number
    state: 'PENDING' | 'ACTIVE' | 'CLOSED' | 'EXPIRED' | 'CANCELLED'
    requestedAt: string
    activatedAt?: string
    acceptedAt?: string
    closeReason?: string
    expiresAt?: string
    closedAt?: string
    profileId?: number
    summary?: string
    closeReason?: string
}
export interface ChronicConsultationSnapshot {
    limit: number
    used: number
    remaining: number
    saleType: string
    periodStart?: string
    periodEnd?: string
    sessions: ChronicConsultation[]
}
export interface ChronicConsultationPolicy {
    monthlyLimit: number
    durationMinutes: number
}
export const useDoctorConsultationStateApi = (orderId: number) =>
    service.get<ChronicConsultationSnapshot>(`/doctor/chronic/consultation/my/${orderId}`)

export const useDoctorConfirmFirstReplyApi = (orderId: number, sessionId: number) =>
    service.post<ChronicConsultationSnapshot>(
        `/doctor/chronic/consultation/my/${orderId}/sessions/${sessionId}/first-reply`)

export const useDoctorCloseConsultationApi = (orderId: number, sessionId: number, summary?: string) =>
    service.post<ChronicConsultationSnapshot>(
        `/doctor/chronic/consultation/my/${orderId}/sessions/${sessionId}/close`, {summary})

export const useChronicConsultationPolicyApi = (packageId: number) =>
    service.get<ChronicConsultationPolicy>(`/doctor/chronic/consultation/policy/${packageId}`)

export const useChronicConsultationPolicySaveApi =
    (packageId: number, data: ChronicConsultationPolicy) =>
    service.put<ChronicConsultationPolicy>(`/doctor/chronic/consultation/policy/${packageId}`, data)

/** Dedicated order-bound acceptance; accepting the card does not spend a consultation. */
export const useAnswerChronicOrderCardApi = (orderId: number, sessionId: number, accept: boolean) =>
    service.post<string>(`/sys/im/answerChronicOrder/${orderId}/${sessionId}/${accept}`)
